import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { PillarCard } from "@/components/calv/PillarCard";
import { CaseCard } from "@/components/calv/CaseCard";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { Callout } from "@/components/calv/Callout";
import { DeltaHero } from "@/components/calv/DeltaHero";
import { LogoReferences } from "@/components/calv/LogoReferences";

export const Route = createFileRoute("/en/services/")({
  head: () => {
    const h = pageHead("services", "en");
    return { meta: h.meta, links: h.links };
  },
  component: ServicesPage,
});

const engagement = [
  {
    label: "Media budget",
    title: "From €3,500 / month",
    body: "Below this threshold systemic optimization doesn't pay back. We'll honestly recommend other paths.",
  },
  {
    label: "Lead time",
    title: "Four to six weeks",
    body: "Audit, tracking setup and data calibration take time. Promises shorter than that we don't keep.",
  },
  {
    label: "Minimum term",
    title: "Four quarters",
    body: "Compound effects only show after three months. Four quarters give the levers time to fully play out.",
  },
  {
    label: "Fees",
    title: "Independent of ad spend",
    body: "Fixed senior retainer plus efficiency share — never tied to media volume. That keeps our incentives aligned with yours.",
  },
];

function ServicesPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="What we do"
          headline="Services"
          lead="Performance marketing, SEO and AI SEO, conversion and web optimization, tracking, strategy as well as web and software development — as one connected craft, run hands-on by the managing director."
          metaStrip={["Since 2013", "MD on every account", "Fees independent of ad budget"]}
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services" }]}
        />

        <Section
          number="02"
          kicker="Our approach"
          headline={<>Services. <em>One shared data layer.</em></>}
          caption="Performance marketing, SEO, conversion optimization, tracking, strategy and development run on one shared data foundation. No separate reports, no internal friction."
        >
          <RevealOnScroll>
            <div className="grid gap-4 md:grid-cols-3">
              <PillarCard cta="Learn more ↗" pillar="Performance Marketing" discipline="Performance Marketing" description="Search, social media, and affiliate performance across Google Ads, Meta, TikTok, LinkedIn and Microsoft Ads — data-driven, no black-box promises." href="/en/services/performance-marketing" />
              <PillarCard cta="Learn more ↗" pillar="Social Media Marketing" discipline="Social Media Marketing" description="Organic content, paid amplification, and community substance across Meta, TikTok, LinkedIn, and Pinterest — as one steering system. Platform strategy, not just ad placement." href="/en/services/social-media-marketing" />
              <PillarCard cta="Learn more ↗" pillar="SEO & AI SEO" discipline="SEO & AI SEO" description="Organic reach in Google, ChatGPT, Perplexity. Sustainable, compounding." href="/en/services/seo-ai-seo" />
              <PillarCard cta="Learn more ↗" pillar="GEO — Generative Engine Optimization" discipline="GEO" description="Named as a source in AI answers — ChatGPT, Perplexity, AI Overviews. Measured via share of answer, without guarantees." href="/en/services/geo-generative-engine-optimization" />
              <PillarCard cta="Learn more ↗" pillar="Conversion & Web" discipline="CRO & Web" description="Conversion and web optimization. Hypothesis-driven, architectural." href="/en/services/conversion-optimization" />
              <PillarCard cta="Learn more ↗" pillar="Tracking & Data Foundation" discipline="Tracking & Data Foundation" description="Data foundation and attribution. GA4, GTM, server-side, BigQuery — set up cleanly, so steering rests on reliable numbers." href="/en/services/tracking-data-foundation" />
              <PillarCard cta="Learn more ↗" pillar="Strategy & Coordination" discipline="Strategy & Coordination" description="Roadmaps that fit operational reality. Senior reviews and four-eyes principle on every engagement." href="/en/services/strategic-coordination" />
              <PillarCard cta="Learn more ↗" pillar="Website Development" discipline="Website Development" description="Performant, conversion-optimized sites on WordPress, WooCommerce, Shopware, and Shopify." href="/en/services/website-development" />
              <PillarCard cta="Learn more ↗" pillar="Digital Service Development" discipline="Digital Service Development" description="Tools, integrations, data pipelines — tailored to client needs instead of off-the-shelf." href="/en/services/digital-development" />
            </div>

            <div className="mt-16">
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Tools</div>
              <h3 className="mt-3 font-display text-2xl text-ink md:text-3xl">Proprietary tools over standard tooling.</h3>
              <div className="mt-6">
                <Foundation title="Tools" sub="Delta · picazzoo · GonzalAds + custom SaaS" meta="Proprietary software over standard tooling" href="/en/services/tools" />
              </div>
            </div>

            <div className="mt-16">
              <CTABlock
                headline={<>From the overview to a <em>concrete conversation.</em></>}
                body="These nine services aren't isolated — they work in combination. Which combination carries for your situation, we clarify in a short initial conversation. We give an initial methodological assessment of whether and which services fit — and tell you openly if we're not the right fit."
                primary={{ label: "Request a strategy conversation", to: "/en/contact" }}
                secondary={{ label: "Or: test the methodical tools without signup", to: "/en/tools" }}
              />
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="03"
          variant="deep"
          kicker="Proprietary tool"
          headline={<>Delta — <em>product data as a lever.</em></>}
          caption="In e-commerce the performance question is almost always a product-data question. DELTA is our feed-management layer, built exactly there — versioned, platform-specific, no external license fees."
        >
          <DeltaHero locale="en" />
        </Section>

        <Section number="04" kicker="Patterns we see repeatedly" headline={<>Three typical constellations — <em>three levers.</em></>}
          caption="No staged hero cases. Three patterns that show up across client projects — and how we resolve them.">
          <div className="grid gap-4 md:grid-cols-3">
            <CaseCard id="Pattern I" category="E-commerce · DACH" headline="Three disciplines, three vendors, one reporting patchwork." body="SEA, SEO and tracking run in parallel, but uncoordinated. We move them into one shared workflow, rebuild tracking server-side and tune product data through DELTA to the catalog." stat={{ value: "20–40 %", label: "typical CER improvement" }} />
            <CaseCard id="Pattern II" category="B2B · SaaS" headline="Volume grows — qualified pipeline doesn't." body="Lead scoring fed back into Google Ads, bottom-funnel clusters steered through CRM data. Media budget stays — its distribution changes." stat={{ value: "MD", label: "operationally on the account" }} />
            <CaseCard id="Pattern III" category="E-commerce" headline="Competitive drift only surfaces in the monthly report." body="Auction-insights and internal margin corridors on one control loop — action lands before the result KPI moves." stat={{ value: "operative", label: "instead of monthly" }} />
          </div>
        </Section>

        <LogoReferences locale="en" number="05" />

        <Section number="06" variant="tinted" kicker="Engagement model"
          headline={<>When a collaboration <em>works.</em></>}
          caption="Four ground rules we put on the table before every collaboration — so no one operates on credit.">
          <div className="grid gap-4 md:grid-cols-2">
            {engagement.map((e) => (
              <RevealOnScroll key={e.label}>
                <div className="h-full rounded-lg border border-rule bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{e.label}</div>
                  <div className="mt-3 font-display text-xl text-ink">{e.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{e.body}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          <Callout>
            We don't earn more when you spend more — and that's by design.
          </Callout>

          <div className="mt-12">
            <CTABlock
              headline={<>Let's talk about your <em>performance.</em></>}
              body="If you want to know where your online marketing is currently leaving potential on the table – and how to change that in a structured way, we're happy to talk."
              primary={{ label: "Get in touch", to: "/en/contact" }}
              secondary={{ label: "About Calvarius", to: "/en/about" }}
            />
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}

function Foundation({ title, sub, meta, href }: { title: string; sub: string; meta: string; href: string }) {
  return (
    <a href={href} className="flex items-center justify-between gap-4 rounded-lg border border-rule bg-white p-6 transition-colors hover:border-cyan">
      <div>
        <div className="font-display text-lg text-ink">{title}</div>
        <div className="mt-1 text-sm text-ink-3">{sub}</div>
      </div>
      <div className="hidden font-mono text-[11px] text-ink-4 md:block">{meta}</div>
    </a>
  );
}
